import SEO from "@/components/SEO";
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function CheckoutSucessoPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState<"loading" | "success" | "pending" | "error">("loading");
  const [mensagem, setMensagem] = useState("");
  const [partituras, setPartituras] = useState<any[]>([]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setMensagem("ID da sessão não encontrado.");
      return;
    }
    liberarPartituras();
  }, [sessionId]);

  async function liberarPartituras() {
    try {
      const { data, error } = await supabase.functions.invoke('liberar-partituras', {
        body: { session_id: sessionId },
      });
      if (error || !data) {
        setStatus("error");
        setMensagem(error?.message || data?.error || "Erro ao liberar partituras.");
        return;
      }
      if (data.success) {
        setStatus("success");
        setMensagem(data.message || "Partituras liberadas com sucesso!");
        if (data.items) setPartituras(data.items);
      } else {
        setStatus("pending");
        setMensagem(data.message || "Aguardando confirmação do pagamento...");
      }
    } catch (err: any) {
      setStatus("error");
      setMensagem(err.message || "Erro ao processar liberação.");
    }
  }

  return (
    <>
      <SEO
        title="Confirmação de Pagamento"
        description="Página de confirmação do pagamento na GodoyProd. Verifique o status da sua compra e acesse suas partituras liberadas."
        keywords="confirmação pagamento, GodoyProd, checkout, partituras liberadas"
        noindex
        canonicalPath="/checkout/sucesso"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />
        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto text-center">
            {status === "loading" && (
              <div className="py-20">
                <div className="w-12 h-12 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                <p className="text-godoy-cream font-medium">Processando seu pagamento...</p>
                <p className="text-godoy-lightgray text-sm mt-2">Aguarde enquanto liberamos suas partituras.</p>
              </div>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-emerald-400" />
                </div>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mb-3">
                  Pagamento Confirmado!
                </h1>
                <p className="text-godoy-lightgray mb-8">{mensagem}</p>

                {partituras.length > 0 && (
                  <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 mb-8 text-left">
                    <h2 className="text-godoy-gold font-semibold text-sm uppercase tracking-wider mb-4">
                      Partituras Liberadas
                    </h2>
                    <div className="space-y-3">
                      {partituras.map((p, i) => (
                        <div key={i} className="flex items-center gap-3 bg-godoy-black/40 rounded-md px-4 py-3">
                          <i className="ri-file-music-line text-godoy-gold" />
                          <span className="text-godoy-cream text-sm">
                            {p.metadata?.product_name || "Partitura"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
                  <Link
                    to="/minha-conta"
                    className="px-8 py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
                  >
                    <i className="ri-music-2-line mr-2" />
                    Ver Minhas Partituras
                  </Link>
                  <Link
                    to="/biblioteca"
                    className="px-6 py-3 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                  >
                    Continuar Comprando
                  </Link>
                </div>
              </>
            )}

            {status === "pending" && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                  <i className="ri-time-line text-3xl text-amber-400" />
                </div>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mb-3">
                  Aguardando Confirmação
                </h1>
                <p className="text-godoy-lightgray mb-8">{mensagem}</p>
                <p className="text-godoy-lightgray/70 text-sm max-w-md mx-auto mb-8">
                  O pagamento pode levar alguns instantes para ser processado. Assim que confirmado,
                  suas partituras aparecerão automaticamente na sua área.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
                  <button
                    onClick={() => window.location.reload()}
                    className="px-8 py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2" />
                    Verificar Novamente
                  </button>
                  <Link
                    to="/minha-conta"
                    className="px-6 py-3 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                  >
                    Ir para Minha Área
                  </Link>
                </div>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                  <i className="ri-error-warning-line text-3xl text-red-400" />
                </div>
                <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mb-3">
                  Algo Deu Errado
                </h1>
                <p className="text-godoy-lightgray mb-8">{mensagem}</p>
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
                  <Link
                    to="/biblioteca"
                    className="px-8 py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
                  >
                    Voltar à Biblioteca
                  </Link>
                  <Link
                    to="/minha-conta"
                    className="px-6 py-3 border border-godoy-gray/40 text-godoy-cream rounded-md hover:border-godoy-gold/40 hover:text-godoy-gold transition-all text-sm whitespace-nowrap"
                  >
                    Minha Área
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
}